/** @jsxImportSource @emotion/react */

import { useContext, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { AlternateAction } from 'components/AlternateAction';
import { ErrorBox } from 'components/ErrorBox';
import { hasMinLength, Input, isRequired, Submit } from 'components/Form';
import { PageHeading } from 'components/Page';
import { Text } from 'components/Text';
import { TrackingContext } from 'context/Tracking';
import { useMfaRedirect } from 'hooks/useMfaRedirect';
import { mfaLoginRoutes } from 'pages/MfaLogin/routes';
import { Endpoints, post } from 'utils/api';
import { useHistory } from 'utils/router';
import { useStyles } from 'utils/useStyles';

import { mfaRecoveryStyles } from './MfaRecovery.styles';

type RecoveryStep = 'request' | 'verify';

interface OtpFormValues {
  otp: string;
}

interface RequestOtpResponse {
  success: boolean;
  expiresInMinutes: number;
}

interface VerifyOtpResponse {
  success: boolean;
}

const formName = 'mfa-recovery';

const formatOtpCode = (value: string): string => {
  return value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8);
};

export const MfaRecoveryForm = () => {
  const styles = useStyles(mfaRecoveryStyles);
  const [, { faro }] = useContext(TrackingContext);
  const history = useHistory();
  const redirectAfterSuccess = useMfaRedirect();

  const [step, setStep] = useState<RecoveryStep>('request');
  const [serverError, setServerError] = useState<string>();
  const [isRequesting, setIsRequesting] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<OtpFormValues>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: { otp: '' },
  });

  const isLoading = isSubmitting || isRequesting;

  const handleRequestOtp = async () => {
    setServerError(undefined);
    setIsRequesting(true);

    try {
      faro.trackFormSubmit(`${formName}-request`);

      await post<RequestOtpResponse>(Endpoints.MFA_REQUEST_RECOVERY_OTP, {});

      faro.trackFormSubmitSuccess(`${formName}-request`);
      setStep('verify');
    } catch (err: unknown) {
      const error = err as { message?: string };
      const errorMessage = error.message || 'Failed to send recovery code. Please try again.';

      faro.trackFormSubmitError(`${formName}-request`, errorMessage);
      faro.trackError(err as Error);
      setServerError(errorMessage);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleOtpSubmit: SubmitHandler<OtpFormValues> = async (values) => {
    setServerError(undefined);

    try {
      faro.trackFormSubmit(`${formName}-verify`);

      await post<VerifyOtpResponse>(Endpoints.MFA_VERIFY_RECOVERY_OTP, {
        otp: values.otp,
      });

      faro.trackFormSubmitSuccess(`${formName}-verify`);
      await redirectAfterSuccess();
    } catch (err: unknown) {
      const error = err as { message?: string };
      const errorMessage = error.message || 'Invalid recovery code. Please try again.';

      faro.trackFormSubmitError(`${formName}-verify`, errorMessage);
      faro.trackError(err as Error);
      setServerError(errorMessage);
      setValue('otp', '');
    }
  };

  const handleBackToMfaLogin = () => {
    faro.trackEvent(`${formName}-back-to-mfa`);
    history.push(mfaLoginRoutes.base);
  };

  return (
    <div css={styles.container}>
      <PageHeading css={styles.title}>
        {step === 'request' ? 'Recover your account' : 'Enter recovery code'}
      </PageHeading>
      <Text align="center" css={styles.description} type="normal">
        {step === 'request'
          ? "We'll send a one-time code to your email address to help you sign in."
          : 'Enter the code we sent to your email address.'}
      </Text>

      {step === 'request' ? (
        <div css={styles.inputContainer}>
          <Submit
            id="request-otp"
            label="Send recovery code"
            isLoading={isRequesting}
            loadingText="Sending..."
            disabled={isRequesting}
            onClick={handleRequestOtp}
            trackingLabel={`${formName}-request`}
          />
          {serverError && <ErrorBox id="server-error">{serverError}</ErrorBox>}
        </div>
      ) : (
        <form onSubmit={handleSubmit(handleOtpSubmit)} noValidate css={styles.form}>
          <div css={[styles.inputContainer, styles.otpInputContainer]}>
            <Input
              control={control}
              errors={errors}
              name="otp"
              label="Recovery code"
              type="text"
              showLabel
              autoFocus
              disabled={isLoading}
              formatter={formatOtpCode}
              placeholder="Enter code"
              validate={[isRequired('Please enter the recovery code'), hasMinLength(8, 'Code must be 8 characters')]}
            />
            <Submit
              id="submit-otp"
              label="Verify"
              isLoading={isSubmitting}
              loadingText="Verifying..."
              disabled={isLoading}
              trackingLabel={`${formName}-verify`}
            />
          </div>
          {serverError && <ErrorBox id="server-error">{serverError}</ErrorBox>}
        </form>
      )}

      <div css={styles.actionsContainer}>
        {step === 'verify' && (
          <AlternateAction
            label="Didn't receive the code?"
            actionLabel="Resend"
            actionEvent={handleRequestOtp}
            id="resend-otp"
          />
        )}
        <AlternateAction
          label=""
          actionLabel="Back to verification"
          actionEvent={handleBackToMfaLogin}
          actionType="button"
          id="back-to-mfa"
        />
      </div>
    </div>
  );
};
